import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, Loader2, X } from 'lucide-react';
import clsx from 'clsx';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function ChatWidget({ onPreview }) {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Hi! 👋 I can help you find candidates or answer questions about your database.' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(scrollToBottom, 200);
        }
    }, [isOpen, messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('query', userMsg);

            const res = await axios.post(`${API_BASE}/chat`, formData);

            const { answer, candidates } = res.data;

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: answer,
                candidates: candidates
            }]);

        } catch (error) {
            console.error("Chat error", error);
            setMessages(prev => [...prev, { role: 'assistant', content: "Connection error. Please try again." }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end pointer-events-none">

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="pointer-events-auto w-[400px] h-[550px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden mb-4"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-brand-600 to-brand-500 p-4 text-white flex items-center justify-between shadow-sm">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-brand-100" />
                                <h3 className="font-bold">AI Assistant</h3>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-brand-100 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Chat Body */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 dark:bg-slate-900/50 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
                            {messages.map((msg, idx) => (
                                <div key={idx} className={clsx("flex", msg.role === 'user' ? "justify-end" : "justify-start")}>
                                    <div className={clsx(
                                        "max-w-[85%] rounded-2xl p-3 text-sm shadow-sm",
                                        msg.role === 'user'
                                            ? "bg-brand-600 text-white rounded-br-none"
                                            : "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-none border border-slate-100 dark:border-slate-700"
                                    )}>
                                        <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>

                                        {msg.candidates && msg.candidates.length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                                                <p className="text-[10px] text-slate-500 mb-1 font-semibold uppercase tracking-wider">References</p>
                                                <div className="space-y-1">
                                                    {msg.candidates.map(c => (
                                                        <div
                                                            key={c._id}
                                                            onClick={() => onPreview(c._id, c.name, c)}
                                                            className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded hover:bg-brand-50 dark:hover:bg-slate-700 cursor-pointer transition-colors border border-transparent hover:border-brand-200"
                                                        >
                                                            <div className="flex items-center gap-2 truncate">
                                                                <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-[10px] font-bold">
                                                                    {c.name ? c.name[0] : '?'}
                                                                </div>
                                                                <span className="text-xs font-medium truncate">{c.name}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="flex justify-start">
                                    <div className="bg-white dark:bg-slate-800 p-3 rounded-2xl rounded-bl-none shadow-sm border border-slate-100 dark:border-slate-700">
                                        <Loader2 className="w-4 h-4 text-brand-600 animate-spin" />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <form onSubmit={handleSend} className="p-3 bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Ask anything..."
                                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                                />
                                <button
                                    type="submit"
                                    disabled={loading || !input.trim()}
                                    className="p-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toggle Button */}
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className="pointer-events-auto bg-brand-600 hover:bg-brand-700 text-white p-4 rounded-full shadow-lg shadow-brand-200/50 dark:shadow-none flex items-center justify-center transition-colors"
            >
                {isOpen ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
            </motion.button>
        </div>
    );
}
